import { useMemo, useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import { products } from "@/mocks/home";

const categories = ["All", "Direct reduced iron", "Construction", "Industrial", "Infrastructure", "Automotive"];

export default function Products() {
  const [active, setActive] = useState("All");
  const [quoteFor, setQuoteFor] = useState<string | null>(null);

  const filtered = useMemo(
    () => (active === "All" ? products : products.filter((p) => p.category === active)),
    [active],
  );

  return (
    <section id="products" className="bg-background-50 py-20 md:py-28">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHeading
            eyebrow="The product"
            title="Green HBI: clean iron for any steel plant"
            description="Hot briquetted iron, made with hydrogen instead of coal. Dense, safe to store and ship, and ready to charge into any furnace."
          />
          <div className="reveal flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setActive(category)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  active === category
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div
          data-product-shop
          className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {filtered.map((product, index) => (
            <article
              key={product.name}
              className="reveal group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100/50 transition-all hover:border-primary-300 hover:bg-background-50"
              style={{ transitionDelay: `${index * 60}ms` }}
            >
              <div className="relative h-60 w-full overflow-hidden bg-background-100">
                <img
                  src={product.image}
                  alt={product.name}
                  title={`${product.name} green steel product`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 rounded-full bg-background-50/95 px-3 py-1 text-xs font-medium text-foreground-700 backdrop-blur">
                  {product.category}
                </span>
                <span className="absolute right-4 top-4 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-primary-950">
                  Green
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-heading text-lg font-semibold text-foreground-950">{product.name}</h3>
                <p className="mt-1 text-sm text-foreground-500">{product.spec}</p>
                <div className="mt-4 flex items-center gap-2 border-t border-background-200 pt-4 text-xs text-foreground-600">
                  <i className="ri-file-shield-2-line text-primary-600" />
                  Standard {product.grade}
                  <span className="ml-auto flex items-center gap-1 text-foreground-500">
                    <i className="ri-shield-star-line text-primary-600" />
                    Carbon passport
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setQuoteFor(product.name)}
                  className="mt-5 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
                >
                  <i className="ri-file-list-3-line" />
                  Request quote
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {quoteFor && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-foreground-950/60 p-4 backdrop-blur-sm sm:items-center"
          onClick={() => setQuoteFor(null)}
        >
          <div
            className="animate-scale-in w-full max-w-md rounded-xl border border-background-200 bg-background-50 p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-file-list-3-line text-xl" />
              </span>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setQuoteFor(null)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <h3 className="mt-4 font-heading text-xl font-semibold text-foreground-950">Request a quote</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              Tell our sales team about your volume and delivery needs for{" "}
              <strong className="font-semibold text-foreground-900">{quoteFor}</strong>. A specialist replies
              within one business day.
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <a
                href="#contact"
                onClick={() => setQuoteFor(null)}
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-center text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                Go to contact form
              </a>
              <button
                type="button"
                onClick={() => setQuoteFor(null)}
                className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
              >
                Keep browsing
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}